import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon, Download, FilterIcon } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { getDefaultAcademicYearRange } from '../../../utils/dashboardDateRange';
import { createReportCsvBlob } from '../../../utils/reportCsvExport';
import { REPORT_TABLE_VALIGN, ReportTh, REPORT_THEAD_ROW, REPORT_TH_INNER } from './ReportPageUi';
import { useReportChartFetchByKey, useReportChartDisplay } from './useReportChartFetch';
import StudentSessionReportCharts from './StudentSessionReportCharts';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000];
const PAGE_SIZE_ALL = 10000;
const CSV_FETCH_PAGE_SIZE = 1000;
const CSV_EXPORT_TIMEOUT_MS = 180000;

function csvEscapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildFlashAnzanStudentSessionsParams(page, pageSize, dateFrom, dateTo) {
  const params = {
    page,
    pageSize: String(pageSize)
  };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  return params;
}

async function fetchAllFlashAnzanStudentSessionRows(studentId, dateFrom, dateTo) {
  const rows = [];
  let pageNum = 1;
  const maxSafetyPages = 500;
  while (pageNum <= maxSafetyPages) {
    const params = buildFlashAnzanStudentSessionsParams(pageNum, CSV_FETCH_PAGE_SIZE, dateFrom, dateTo);
    const response = await api.get(`/reports/flash-anzan/students/${encodeURIComponent(studentId)}/sessions`, {
      params,
      timeout: CSV_EXPORT_TIMEOUT_MS
    });
    if (!response.data?.success) {
      const msg = response.data?.message || response.data?.error;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg || 'export failed'));
    }
    const data = response.data.data || {};
    const batch = data.items || [];
    rows.push(...batch);
    if (batch.length === 0) break;
    if (batch.length < CSV_FETCH_PAGE_SIZE) break;
    pageNum += 1;
  }
  return rows;
}

const emptyStudentMeta = () => ({
  studentId: null,
  studentName: null,
  fullName: null,
  firstName: null,
  lastName: null,
  username: null,
  studentNumber: null
});

function pickDisplayName(meta) {
  if (meta.studentName && String(meta.studentName).trim()) return String(meta.studentName).trim();
  if (meta.fullName && String(meta.fullName).trim()) return String(meta.fullName).trim();
  const fn = (meta.firstName || '').trim();
  const ln = (meta.lastName || '').trim();
  const combined = [fn, ln].filter(Boolean).join(' ').trim();
  return combined || '';
}

const ReportFlashAnzanStudentSessions = () => {
  const { studentId: studentIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const defaultRange = getDefaultAcademicYearRange();

  const studentId = studentIdParam != null ? String(studentIdParam).trim() : '';

  const initialFrom = searchParams.get('dateFrom') || defaultRange.dateFrom;
  const initialTo = searchParams.get('dateTo') || defaultRange.dateTo;

  const [draft, setDraft] = useState({
    dateFrom: initialFrom,
    dateTo: initialTo
  });
  const [applied, setApplied] = useState({
    dateFrom: initialFrom,
    dateTo: initialTo
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [studentMeta, setStudentMeta] = useState(emptyStudentMeta);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
  const [period, setPeriod] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);

  const chartFiltersKey = useMemo(
    () => [studentId, applied.dateFrom, applied.dateTo].join('|'),
    [studentId, applied.dateFrom, applied.dateTo]
  );

  const loadAllRowsForCharts = useCallback(
    () => fetchAllFlashAnzanStudentSessionRows(studentId, applied.dateFrom, applied.dateTo),
    [studentId, applied.dateFrom, applied.dateTo]
  );

  const { chartRows, chartLoading } = useReportChartFetchByKey(chartFiltersKey, loadAllRowsForCharts);
  const { rowsForCharts, chartsShowLoading, partial: chartsPartial } = useReportChartDisplay(
    chartRows,
    chartLoading,
    items,
    loading
  );

  const formatMs = (ms) => {
    if (ms == null || ms === '') return '—';
    const n = Number(ms);
    if (Number.isNaN(n)) return String(ms);
    const sec = Math.round(n / 1000);
    if (sec < 60) return `${sec} ${t('reports.seconds')}`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')} ${t('reports.minutes')}`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const locale = currentLanguage === 'ar' ? 'ar' : currentLanguage === 'fr' ? 'fr' : 'en';
      return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return String(iso);
    }
  };

  const displayName = useMemo(() => pickDisplayName(studentMeta), [studentMeta]);

  const bandLabel = (band) => {
    if (!band || typeof band !== 'string') return '—';
    const k = band.toLowerCase();
    const key = `reports.dailyExercisesApi.sessionBand_${k}`;
    const translated = t(key);
    return translated === key ? band : translated;
  };

  const fetchData = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params = buildFlashAnzanStudentSessionsParams(page, pageSize, applied.dateFrom, applied.dateTo);
      const response = await api.get(`/reports/flash-anzan/students/${encodeURIComponent(studentId)}/sessions`, {
        params
      });
      if (response.data.success) {
        const data = response.data.data || {};
        setStudentMeta({
          studentId: data.studentId != null ? data.studentId : studentId || null,
          studentName: data.studentName ?? null,
          fullName: data.fullName ?? null,
          firstName: data.firstName ?? null,
          lastName: data.lastName ?? null,
          username: data.username ?? null,
          studentNumber: data.studentNumber ?? null
        });
        setItems(data.items || []);
        const p = data.pagination || {};
        setPagination({
          page: Number(p.page) || page,
          pageSize: Number(p.pageSize) || pageSize,
          total: Number(p.total) || 0,
          totalPages: Number(p.totalPages) || 0
        });
        setPeriod(data.period || null);
      } else {
        setItems([]);
        setStudentMeta(emptyStudentMeta());
        setPagination({ page: 1, pageSize, total: 0, totalPages: 0 });
        setPeriod(null);
      }
    } catch (error) {
      const resData = error.response?.data;
      const serverMsg = resData?.message || resData?.error || resData?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      if (detail) toast.error(String(detail), { duration: 5000, position: 'top-center' });
      else toast.error(t('reports.fetchError'), { duration: 3000, position: 'top-center' });
      setItems([]);
      setStudentMeta(emptyStudentMeta());
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied.dateFrom, applied.dateTo, studentId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFiltersHandler = () => {
    setApplied({ ...draft });
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('dateFrom', draft.dateFrom);
        next.set('dateTo', draft.dateTo);
        return next;
      },
      { replace: true }
    );
    setShowFilters(false);
  };

  const listLink = () => {
    const q = new URLSearchParams();
    q.set('dateFrom', applied.dateFrom);
    q.set('dateTo', applied.dateTo);
    return `/dashboard/reports/flash-anzan?${q.toString()}`;
  };

  const toggleExpand = (sessionId) => {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  };

  const downloadCsv = async () => {
    if (exportingCsv || !studentId) return;
    setExportingCsv(true);
    try {
      const allSessions = await fetchAllFlashAnzanStudentSessionRows(studentId, applied.dateFrom, applied.dateTo);
      if (allSessions.length === 0) {
        toast.error(t('reports.users.csvNoRows'), { position: 'top-center' });
        return;
      }
      const exportName = pickDisplayName(studentMeta) || '';
      const periodStr =
        period?.dateFrom && period?.dateTo
          ? `${period.dateFrom} → ${period.dateTo}`
          : [applied.dateFrom, applied.dateTo].filter(Boolean).join(' → ');

      const metaRows = [
        [t('reports.users.colFullName'), exportName],
        [t('reports.users.colStudentNo'), studentMeta.studentNumber != null ? String(studentMeta.studentNumber) : ''],
        [t('reports.users.colUsername'), studentMeta.username != null ? String(studentMeta.username) : ''],
        ['ID', studentMeta.studentId != null ? String(studentMeta.studentId) : ''],
        [t('reports.dailyExercisesApi.period'), periodStr]
      ];

      const sessionHeaders = [
        t('reports.dailyExercisesApi.colSessionId'),
        t('reports.dailyExercisesApi.colSessionStatus'),
        t('reports.dailyExercisesApi.colSessionScorePct'),
        t('reports.dailyExercisesApi.colSuccessBand'),
        t('reports.dailyExercisesApi.colTimingMs'),
        t('reports.dailyExercisesApi.colOperationType'),
        t('reports.dailyExercisesApi.colStartedAt'),
        t('reports.dailyExercisesApi.colCompletedAt'),
        t('reports.dailyExercisesApi.colWrongOpsCount')
      ];

      const lines = [];
      for (const r of metaRows) {
        lines.push(r.map(csvEscapeCell).join(','));
      }
      lines.push('');
      lines.push(sessionHeaders.map(csvEscapeCell).join(','));

      for (const row of allSessions) {
        const wrongs = row.wrong_operations || [];
        const band = row.success_band && typeof row.success_band === 'string'
          ? (() => {
              const k = row.success_band.toLowerCase();
              const key = `reports.dailyExercisesApi.sessionBand_${k}`;
              const tr = t(key);
              return tr === key ? row.success_band : tr;
            })()
          : '';
        const line = [
          row.session_id || '',
          row.status || '',
          row.session_score_pct ?? '',
          band,
          formatMs(row.timing_ms),
          row.operation_type || '',
          row.started_at || '',
          row.completed_at || '',
          wrongs.length
        ].map(csvEscapeCell).join(',');
        lines.push(line);
      }

      const wrongHeaders = [
        t('reports.dailyExercisesApi.colSessionId'),
        t('reports.dailyExercisesApi.colOperationType'),
        t('reports.dailyExercisesApi.colStudentAnswer'),
        t('reports.dailyExercisesApi.colCorrectAnswer'),
        t('reports.dailyExercisesApi.colOpScore')
      ];
      lines.push('');
      lines.push(csvEscapeCell(t('reports.dailyExercisesApi.wrongOperationsTitle')));
      lines.push(wrongHeaders.map(csvEscapeCell).join(','));

      for (const row of allSessions) {
        const wrongs = row.wrong_operations || [];
        const sid = row.session_id || '';
        for (const op of wrongs) {
          const wline = [
            op.session_id || sid,
            op.operation_type || '',
            op.student_answer == null ? '' : String(op.student_answer),
            op.correct_answer == null ? '' : String(op.correct_answer),
            op.score ?? ''
          ].map(csvEscapeCell).join(',');
          lines.push(wline);
        }
      }

      const csvBody = lines.join('\r\n');
      const blob = createReportCsvBlob(csvBody);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const safeName = exportName
        ? exportName.replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, '_').slice(0, 80)
        : String(studentMeta.studentId ?? studentId);
      a.href = url;
      a.download = `flash-anzan-student-${safeName}-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('reports.users.csvSuccess', { count: allSessions.length }), { position: 'top-center' });
    } catch (err) {
      const res = err.response?.data;
      const serverMsg = res?.message ?? res?.error ?? res?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      const axiosMsg = err.code === 'ECONNABORTED' || err.message?.includes('timeout')
        ? t('reports.users.csvTimeout')
        : null;
      toast.error(axiosMsg || (detail ? String(detail) : err.message) || t('reports.users.csvError'), { duration: 6000, position: 'top-center' });
    } finally {
      setExportingCsv(false);
    }
  };

  const canExportCsv =
    !loading && !exportingCsv && studentId && (pagination.total > 0 || items.length > 0);

  if (!studentId) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-sm shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
        <p className="text-gray-600">{t('reports.noData')}</p>
        <Link to="/dashboard/reports/flash-anzan" className="mt-4 inline-flex items-center gap-2 text-primary-600 text-sm font-medium">
          <ArrowLeftIcon className="h-4 w-4" />
          {t('reports.flashAnzanApi.studentSessionsBack')}
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <Link
            to={listLink()}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t('reports.flashAnzanApi.studentSessionsBack')}
          </Link>
        </div>
        {displayName ? (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('reports.flashAnzanApi.studentSessionsPageTitle')}
            </p>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{displayName}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
              {studentMeta.studentNumber ? (
                <span>
                  {t('reports.users.colStudentNo')}:{' '}
                  <span className="font-mono text-gray-800">{studentMeta.studentNumber}</span>
                </span>
              ) : null}
              {studentMeta.username ? (
                <span>
                  {t('reports.users.colUsername')}:{' '}
                  <span className="font-medium text-gray-800">{studentMeta.username}</span>
                </span>
              ) : null}
              {studentMeta.studentId != null && studentMeta.studentId !== '' ? (
                <span className="text-gray-600">
                  ID: <span className="tabular-nums">{studentMeta.studentId}</span>
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <h2 className="text-lg font-semibold text-gray-900">
            {t('reports.flashAnzanApi.studentSessionsPageTitle')}
            {studentMeta.studentId != null && studentMeta.studentId !== '' ? (
              <span className="text-gray-600 font-normal">
                {' '}
                — ID {studentMeta.studentId}
              </span>
            ) : null}
          </h2>
        )}
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <FilterIcon className="h-4 w-4" />
          {t('common.filters')}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateFrom')}</label>
              <input
                type="date"
                value={draft.dateFrom}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateTo')}</label>
              <input
                type="date"
                value={draft.dateTo}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateTo: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-sm"
              />
            </div>
          </div>
        )}
        {showFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={applyFiltersHandler}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <FilterIcon className="h-4 w-4" />
              {t('reports.applyFilters')}
            </button>
          </div>
        )}
      </div>

      <div className="p-6">
        {period?.dateFrom && period?.dateTo && (
          <p className="text-sm text-gray-600 mb-4">
            {t('reports.dailyExercisesApi.period')}: {period.dateFrom} → {period.dateTo}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-500 text-center py-12">{t('reports.noData')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
              <button
                type="button"
                onClick={downloadCsv}
                disabled={!canExportCsv}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                {exportingCsv ? t('reports.users.csvExporting') : t('reports.users.downloadCsv')}
              </button>
            </div>
            <StudentSessionReportCharts
              preset="flashAnzan"
              rows={rowsForCharts}
              loading={chartsShowLoading}
              partial={chartsPartial}
              t={t}
            />
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="overflow-x-auto max-w-full">
                <table className={`min-w-[1000px] w-full text-sm ${REPORT_TABLE_VALIGN}`}>
                  <thead>
                    <tr className={REPORT_THEAD_ROW}>
                      <th className="w-10 align-middle py-3 px-2" aria-label={t('reports.dailyExercisesApi.colExpand')} />
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colSessionId')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colSessionStatus')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colSessionScorePct')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colSuccessBand')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colTimingMs')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colOperationType')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colStartedAt')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colCompletedAt')}</ReportTh>
                      <ReportTh className="!py-3 !px-2">{t('reports.dailyExercisesApi.colWrongOpsCount')}</ReportTh>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {items.map((row, idx) => {
                      const sid = row.session_id || '';
                      const wrongs = row.wrong_operations || [];
                      const isOpen = expandedSessionId === sid;
                      return (
                        <React.Fragment key={sid || `fa-session-${idx}`}>
                          <tr className="hover:bg-gray-50/80">
                            <td className="py-2 px-1 align-middle">
                              <button
                                type="button"
                                onClick={() => sid && toggleExpand(sid)}
                                className="p-1 rounded text-gray-600 hover:bg-gray-200"
                                aria-expanded={isOpen}
                                disabled={!sid || wrongs.length === 0}
                              >
                                {wrongs.length === 0 ? (
                                  <span className="inline-block w-5" />
                                ) : isOpen ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                              </button>
                            </td>
                            <td className="py-3 px-2 text-gray-800 font-mono text-xs">{sid || '—'}</td>
                            <td className="py-3 px-2 text-gray-700">{row.status || '—'}</td>
                            <td className="py-3 px-2 text-gray-700 tabular-nums">{row.session_score_pct ?? '—'}</td>
                            <td className="py-3 px-2 text-gray-700">{bandLabel(row.success_band)}</td>
                            <td className="py-3 px-2 text-gray-700 tabular-nums">{formatMs(row.timing_ms)}</td>
                            <td className="py-3 px-2 text-gray-700">{row.operation_type || '—'}</td>
                            <td className="py-3 px-2 text-gray-700 whitespace-nowrap">{formatDateTime(row.started_at)}</td>
                            <td className="py-3 px-2 text-gray-700 whitespace-nowrap">{formatDateTime(row.completed_at)}</td>
                            <td className="py-3 px-2 text-gray-700 tabular-nums">{wrongs.length}</td>
                          </tr>
                          {isOpen && wrongs.length > 0 && (
                            <tr className="bg-gray-50/90">
                              <td colSpan={10} className="p-0 border-t border-gray-200">
                                <div className="p-4">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">
                                    {t('reports.dailyExercisesApi.wrongOperationsTitle')}
                                  </p>
                                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <table className={`min-w-[560px] w-full text-xs ${REPORT_TABLE_VALIGN}`}>
                                      <thead>
                                        <tr className="bg-gray-100">
                                          <th className="py-2 px-2 align-middle text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            <span className={REPORT_TH_INNER}>{t('reports.dailyExercisesApi.colOperationType')}</span>
                                          </th>
                                          <th className="py-2 px-2 align-middle text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            <span className={REPORT_TH_INNER}>{t('reports.dailyExercisesApi.colStudentAnswer')}</span>
                                          </th>
                                          <th className="py-2 px-2 align-middle text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            <span className={REPORT_TH_INNER}>{t('reports.dailyExercisesApi.colCorrectAnswer')}</span>
                                          </th>
                                          <th className="py-2 px-2 align-middle text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            <span className={REPORT_TH_INNER}>{t('reports.dailyExercisesApi.colOpScore')}</span>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {wrongs.map((op, widx) => (
                                          <tr key={`${op.session_id || sid}-${widx}`} className="border-t border-gray-100">
                                            <td className="py-2 px-2">{op.operation_type || '—'}</td>
                                            <td className="py-2 px-2 font-mono tabular-nums">
                                              {op.student_answer == null ? '—' : String(op.student_answer)}
                                            </td>
                                            <td className="py-2 px-2 font-mono tabular-nums">
                                              {op.correct_answer == null ? '—' : String(op.correct_answer)}
                                            </td>
                                            <td className="py-2 px-2 tabular-nums">{op.score ?? '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <p>
                {t('reports.flashAnzanApi.studentSessionsPageInfo', {
                  page: pagination.page,
                  totalPages: pagination.totalPages || 1,
                  total: pagination.total
                })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2">
                  <span>{t('reports.users.pageSize')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="rounded-md border border-gray-300 py-1.5 px-2 text-sm min-w-[10rem]"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    <option value={PAGE_SIZE_ALL}>{t('reports.users.pageSizeOptionAll')}</option>
                  </select>
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.prev')}
                  </button>
                  <button
                    type="button"
                    disabled={page >= (pagination.totalPages || 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.next')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportFlashAnzanStudentSessions;
